/**
 * Onboarding service
 * Handles user onboarding, profile updates, and related operations
 */

import axios from "axios";
import api from "../api";

export type OnboardingData = {
  // Step 1: Professional Identity
  job_title?: string | null;
  primary_specialty?: string | null;
  secondary_specialties?: string[];
  years_of_experience?: number | null;
  industry_focus?: string | null;
  bio?: string | null;

  // Step 2: Work Context
  hr_team_size?: string | null;
  work_type?: string | null;
  previous_company?: string | null;
  location?: string | null;
  country?: string | null;
  state_location?: string | null;
  salary_frequency?: string | null;
  current_salary?: number | null;
  salary_currency?: string | null;
  salary_type?: string | null;
  salary_not_disclosed?: boolean;
  additional_compensation?: string[];

  // Step 3: Goals & Intent
  motivations?: string[];
  current_challenge?: string | null;
  job_hunting_status?: string | null;
  platform_goal?: string | null;

  // Legacy fields (kept for backward compatibility)
  experience_level?: string | null;
  operation_type?: string | null;
  primary_industries?: string | null;
  secondary_industries?: string | null;
  specialty?: string | null;
  professional_certification?: string | null;
  professional_communities?: string | null;
  how_you_heard_about_us?: string | null;

  // Control field
  onboarding_completed: boolean;
};

function normalizeOnboardingPayload(payload: Partial<OnboardingData> | undefined): OnboardingData {
  const toNullableString = (value: unknown): string | null => {
    if (value === null || value === undefined) {
      return null;
    }
    const str = String(value).trim();
    return str.length ? str : null;
  };

  const toNullableNumber = (value: unknown): number | null => {
    if (value === null || value === undefined || value === "") {
      return null;
    }
    const num = Number(value);
    return Number.isNaN(num) ? null : num;
  };

  const toArray = (value: unknown): string[] => {
    if (Array.isArray(value)) {
      return value.filter((v) => typeof v === "string" && v.trim().length > 0);
    }
    return [];
  };

  const current_salary = toNullableNumber(payload?.current_salary);

  let years_of_experience: number | null = null;
  const yearsValue = payload?.years_of_experience;
  if (yearsValue !== null && yearsValue !== undefined) {
    const parsedYears = Number.parseInt(String(yearsValue), 10);
    if (!Number.isNaN(parsedYears)) {
      years_of_experience = parsedYears;
    }
  }

  return {
    // Step 1: Professional Identity
    job_title: toNullableString(payload?.job_title),
    primary_specialty: toNullableString(payload?.primary_specialty),
    secondary_specialties: toArray(payload?.secondary_specialties),
    years_of_experience,
    industry_focus: toNullableString(payload?.industry_focus),
    bio: toNullableString(payload?.bio),

    // Step 2: Work Context
    hr_team_size: toNullableString(payload?.hr_team_size),
    work_type: toNullableString(payload?.work_type),
    previous_company: toNullableString(payload?.previous_company),
    location: toNullableString(payload?.location),
    country: toNullableString(payload?.country),
    state_location: toNullableString(payload?.state_location),
    salary_frequency: toNullableString(payload?.salary_frequency),
    current_salary,
    salary_currency: toNullableString(payload?.salary_currency),
    salary_type: toNullableString(payload?.salary_type),
    salary_not_disclosed: Boolean(payload?.salary_not_disclosed),
    additional_compensation: toArray(payload?.additional_compensation),

    // Step 3: Goals & Intent
    motivations: toArray(payload?.motivations),
    current_challenge: toNullableString(payload?.current_challenge),
    job_hunting_status: toNullableString(payload?.job_hunting_status),
    platform_goal: toNullableString(payload?.platform_goal),

    // Legacy fields
    experience_level: toNullableString(payload?.experience_level),
    operation_type: toNullableString(payload?.operation_type),
    primary_industries: toNullableString(payload?.primary_industries),
    secondary_industries: toNullableString(payload?.secondary_industries),
    specialty: toNullableString(payload?.specialty),
    professional_certification: toNullableString(payload?.professional_certification),
    professional_communities: toNullableString(payload?.professional_communities),
    how_you_heard_about_us: toNullableString(payload?.how_you_heard_about_us),

    // Control field
    onboarding_completed: Boolean(payload?.onboarding_completed),
  };
}

/**
 * Get onboarding completion status
 */
export async function getOnboardingStatus() {
  try {
    const response = await api.get("/api/account/onboarding-status/");
    return response.data as { onboarding_completed: boolean };
  } catch (error: unknown) {
    if (axios.isAxiosError(error)) {
      throw new Error(error.response?.data?.error || error.message);
    }
    throw new Error("An unknown error occured!");
  }
}

/**
 * Get full onboarding data
 */
export async function getOnboardingData(): Promise<OnboardingData> {
  try {
    const response = await api.get("/api/account/onboarding/");
    return normalizeOnboardingPayload(response.data as Partial<OnboardingData> | undefined);
  } catch (error: unknown) {
    if (axios.isAxiosError(error)) {
      const data = error.response?.data as { detail?: string; error?: string } | undefined;
      throw new Error(data?.detail || data?.error || error.message);
    }
    throw new Error("An unknown error occured!");
  }
}

/**
 * Update onboarding data
 */
export async function updateOnboarding(data: Partial<Omit<OnboardingData, 'onboarding_completed'>>): Promise<OnboardingData> {
  try {
    const response = await api.put("/api/account/onboarding/", data);
    return normalizeOnboardingPayload(response.data as Partial<OnboardingData> | undefined);
  } catch (error: unknown) {
    if (axios.isAxiosError(error)) {
      const errorData = error.response?.data as { code?: string; message?: string; error?: string; fields?: Record<string, string[]> } | undefined;
      // Include the error code and field errors for better error handling
      const err = new Error(errorData?.message || errorData?.error || error.message);
      (err as any).code = errorData?.code;
      (err as any).fields = errorData?.fields;
      throw err;
    }
    throw new Error("An unknown error occured!");
  }
}

function serializeUpdateUserPayload(input: FormData | { username?: string; email?: string }): Record<string, unknown> {
  if (input instanceof FormData) {
    const result: Record<string, unknown> = {};
    input.forEach((value, key) => {
      if (typeof File !== "undefined" && value instanceof File) {
        return;
      }
      if (value === undefined || value === null) {
        return;
      }
      result[key] = value;
    });
    return result;
  }

  const entries = Object.entries(input ?? {});
  const result: Record<string, unknown> = {};
  for (const [key, value] of entries) {
    if (value === undefined || value === null) {
      continue;
    }
    result[key] = value;
  }
  return result;
}

/**
 * Update user profile information
 */
export async function updateUser(data: FormData | { username?: string; email?: string }) {
  try {
    const payload = serializeUpdateUserPayload(data);
    const response = await api.patch("/api/users/me/", payload);
    return response.data as { id: string; email: string; username: string };
  } catch (error: unknown) {
    if (axios.isAxiosError(error)) {
      const body = error.response?.data as { detail?: string; error?: string } | undefined;
      throw new Error(body?.detail || body?.error || error.message);
    }
    throw new Error("An unknown error occured!");
  }
}

/**
 * Upload profile picture
 */
export async function uploadProfilePicture(formData: FormData): Promise<{ profile_picture: string }> {
  try {
    const response = await api.post("/api/account/profile-picture/", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
    return response.data as { profile_picture: string };
  } catch (error: unknown) {
    if (axios.isAxiosError(error)) {
      const body = error.response?.data as { detail?: string; error?: string } | undefined;
      throw new Error(body?.detail || body?.error || error.message);
    }
    throw new Error("Failed to upload profile picture!");
  }
}
